import type { SpaceId } from '@respaldos/shared';
import type { ActivityItem, FileItem, UploadProgress } from '../lib/types';
import { getSpaceMeta } from '../lib/api';
import { isPreviewable } from '../lib/spaceMeta';
import {
  IconArchive,
  IconClose,
  IconDownload,
  IconGrid,
  IconList,
  IconNewFolder,
  IconSearch,
  IconUpload,
  FileTypeIcon,
} from './Icons';
import { TierBadge } from './TierBadge';
import { useRef, useState, type FormEvent } from 'react';

const ACTIVITY_LABELS: Record<string, string> = {
  upload: 'Subida',
  delete: 'Eliminación',
  restore: 'Restauración',
  mkdir: 'Carpeta',
  move: 'Movimiento',
  archive: 'Archivo',
  download: 'Descarga',
  list: 'Visitado',
};

interface FilesProps {
  spaceId: SpaceId;
  files: FileItem[];
  selectedKeys: Set<string>;
  currentPath: string;
  onNavigate: (relPath: string) => void;
  onOpenFolder: (folder: FileItem) => void;
  onPreviewFile: (file: FileItem) => void;
  onToggleSelect: (key: string) => void;
  onSelectAll: (checked: boolean) => void;
  onUpload: (files: FileList | null) => void;
  uploadOpen: boolean;
  onToggleUpload: (open: boolean) => void;
  onNewFolder: () => void;
  onDownload: () => void;
  onMove: () => void;
  onDelete: () => void;
  search: string;
  onSearch: (query: string) => void;
  busy?: boolean;
  listLoading?: boolean;
  uploadProgress?: UploadProgress | null;
}

export function FilesView({
  spaceId,
  files,
  selectedKeys,
  currentPath,
  onNavigate,
  onOpenFolder,
  onPreviewFile,
  onToggleSelect,
  onSelectAll,
  onUpload,
  uploadOpen,
  onToggleUpload,
  onNewFolder,
  onDownload,
  onMove,
  onDelete,
  search,
  onSearch,
  busy,
  listLoading,
  uploadProgress,
}: FilesProps) {
  const meta = getSpaceMeta(spaceId, currentPath);
  const filtered = search.trim()
    ? files.filter(
        (f) =>
          f.name.toLowerCase().includes(search.toLowerCase()) ||
          f.path.toLowerCase().includes(search.toLowerCase()),
      )
    : files;

  const crumbs = meta.crumbs;
  const hasSelection = selectedKeys.size > 0;

  return (
    <>
      <nav className="breadcrumb" aria-label="Ruta">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          const targetPath =
            i === 0
              ? ''
              : crumbs.slice(1, i + 1).join('/');
          return (
            <span key={`${c}-${i}`}>
              {i > 0 && <span className="sep">/</span>}
              {isLast ? (
                <strong>{c}</strong>
              ) : (
                <button type="button" onClick={() => onNavigate(targetPath)}>
                  {i === 0 ? <strong>{c}</strong> : c}
                </button>
              )}
            </span>
          );
        })}
      </nav>

      <div className="toolbar">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => onToggleUpload(true)}
          disabled={meta.readOnly || busy}
          title={meta.readOnly ? 'Espacio de solo lectura' : undefined}
        >
          <IconUpload />
          Subir
        </button>
        <button
          className="btn"
          type="button"
          disabled={meta.readOnly || busy}
          onClick={onNewFolder}
          title={meta.readOnly ? 'Espacio de solo lectura' : 'Crear carpeta aquí'}
        >
          <IconNewFolder />
          Nueva carpeta
        </button>
        <button
          className="btn"
          type="button"
          disabled={!hasSelection || busy}
          onClick={onDownload}
        >
          <IconDownload />
          Descargar
        </button>
        <button
          className="btn"
          type="button"
          disabled={!hasSelection || meta.readOnly || busy}
          onClick={onMove}
          title="Mover dentro de este espacio"
        >
          <IconArchive />
          Mover
        </button>
        <button
          className="btn"
          type="button"
          disabled={
            !hasSelection || meta.readOnly || spaceId !== 'marketing' || busy
          }
          onClick={onDelete}
          title="Mover a papelera"
        >
          Eliminar
        </button>
        <div className="toolbar-spacer" />
        <div className="toolbar-search">
          <IconSearch />
          <input
            type="search"
            placeholder="Buscar archivos o carpetas…"
            aria-label="Buscar archivos"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        {uploadProgress && (
          <div className="upload-progress" aria-live="polite">
            <span>
              Subiendo {uploadProgress.index}/{uploadProgress.total}:{' '}
              {uploadProgress.fileName}
            </span>
            <div className="upload-progress-bar">
              <div
                className="upload-progress-fill"
                style={{ width: `${uploadProgress.percent}%` }}
              />
            </div>
            <span className="upload-progress-pct">{uploadProgress.percent}%</span>
          </div>
        )}
        <div className="view-toggle" title="Vista">
          <button type="button" title="Cuadrícula">
            <IconGrid />
          </button>
          <button type="button" className="active" title="Lista">
            <IconList />
          </button>
        </div>
      </div>

      <div className="info-banner">
        {meta.banner.parts.map((p, i) => (
          <span key={i} style={{ display: 'contents' }}>
            {i > 0 && <span className="dot-sep">·</span>}
            {p.strong ? <strong>{p.strong} </strong> : null}
            {p.code ? <code>{p.code}</code> : null}
            {p.text ? <span>{p.text}</span> : null}
          </span>
        ))}
      </div>

      <div className={`panel ${listLoading ? 'panel-loading' : ''}`}>
        {listLoading && files.length === 0 ? (
          <div className="list-skeleton" aria-busy="true">
            <div className="skel-row" />
            <div className="skel-row" />
            <div className="skel-row" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No hay archivos en esta carpeta.</div>
        ) : (
          <table className="file-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="check"
                    aria-label="Seleccionar todo"
                    checked={
                      filtered.length > 0 &&
                      filtered.every((f) => selectedKeys.has(f.key))
                    }
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                </th>
                <th>Nombre</th>
                <th>Tamaño</th>
                <th>Modificado</th>
                <th>Subido por</th>
                <th>Nivel</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const canOpen = f.isFolder || isPreviewable(f);
                function openItem() {
                  if (f.isFolder) onOpenFolder(f);
                  else if (isPreviewable(f)) onPreviewFile(f);
                }
                return (
                <tr
                  key={f.key}
                  className={[
                    selectedKeys.has(f.key) ? 'selected' : '',
                    canOpen ? 'openable' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    if (canOpen) openItem();
                    else onToggleSelect(f.key);
                  }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="check"
                      checked={selectedKeys.has(f.key)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => onToggleSelect(f.key)}
                    />
                  </td>
                  <td className={canOpen ? 'name-td' : undefined}>
                    {canOpen ? (
                      <button
                        type="button"
                        className="name-cell name-open"
                        onClick={(e) => {
                          e.stopPropagation();
                          openItem();
                        }}
                      >
                        <div className={`file-icon ${f.type}`}>
                          <FileTypeIcon type={f.type} />
                        </div>
                        <div className="name-path">
                          <span className="primary folder-link">{f.name}</span>
                          <span className="secondary">
                            {f.isFolder ? 'Carpeta' : friendlySecondary(f)}
                          </span>
                        </div>
                      </button>
                    ) : (
                      <div className="name-cell">
                        <div className={`file-icon ${f.type}`}>
                          <FileTypeIcon type={f.type} />
                        </div>
                        <div className="name-path">
                          <span className="primary">{f.name}</span>
                          <span className="secondary">
                            {friendlySecondary(f)}
                          </span>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="muted">{f.sizeLabel}</td>
                  <td className="muted">{f.modified}</td>
                  <td className="muted">{f.by}</td>
                  <td>
                    <TierBadge tier={f.tier} />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {uploadOpen && (
        <UploadCard
          title={meta.uploadTitle}
          note={meta.uploadNote}
          disabled={busy}
          onClose={() => onToggleUpload(false)}
          onFiles={onUpload}
        />
      )}
    </>
  );
}

function friendlySecondary(f: FileItem): string {
  if (f.type === 'image') return 'Imagen';
  if (f.type === 'video') return 'Video';
  if (f.type === 'zip') return 'ZIP';
  if (f.type === 'pdf') return 'PDF';
  if (f.type === 'bak') return 'Respaldo';
  return 'Archivo';
}

function UploadCard({
  title,
  note,
  onClose,
  onFiles,
  disabled,
}: {
  title: string;
  note: string;
  onClose: () => void;
  onFiles: (files: FileList | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  return (
    <div className="upload-card">
      <div className="upload-head">
        <h3>{title}</h3>
        <button
          className="icon-btn"
          onClick={onClose}
          aria-label="Cerrar"
          type="button"
          disabled={disabled}
        >
          <IconClose />
        </button>
      </div>
      <div className="upload-body">
        <div
          className={`dropzone ${drag ? 'dragover' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            if (!disabled) onFiles(e.dataTransfer.files);
          }}
        >
          <div className="dz-icon">
            <IconUpload />
          </div>
          <p>{disabled ? 'Subiendo…' : 'Arrastra archivos aquí'}</p>
          <span>o haz clic para seleccionar · imagen, video, ZIP, PDF</span>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            disabled={disabled}
            onChange={(e) => onFiles(e.target.files)}
          />
        </div>
        <div className="upload-note">{note}</div>
      </div>
    </div>
  );
}

export function PreviewModal({
  file,
  url,
  loading,
  error,
  onClose,
  onDownload,
}: {
  file: FileItem;
  url: string | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onDownload: () => void;
}) {
  const kind =
    file.type === 'image' || /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name)
      ? 'image'
      : file.type === 'video' || /\.(mp4|webm|mov)$/i.test(file.name)
        ? 'video'
        : 'pdf';

  return (
    <div className="dialog-backdrop preview-backdrop" role="presentation" onClick={onClose}>
      <div
        className="preview-card"
        role="dialog"
        aria-label={`Vista previa: ${file.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="preview-head">
          <h3>{file.name}</h3>
          <div className="preview-actions">
            <button type="button" className="btn" onClick={onDownload} disabled={!url}>
              <IconDownload />
              Descargar
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              aria-label="Cerrar"
            >
              <IconClose />
            </button>
          </div>
        </div>
        <div className="preview-body">
          {loading && <div className="empty-state">Cargando vista previa…</div>}
          {error && <div className="error-banner">{error}</div>}
          {!loading && !error && url && kind === 'image' && (
            <img src={url} alt={file.name} className="preview-media" />
          )}
          {!loading && !error && url && kind === 'video' && (
            <video src={url} controls className="preview-media" />
          )}
          {!loading && !error && url && kind === 'pdf' && (
            <iframe title={file.name} src={url} className="preview-frame" />
          )}
        </div>
      </div>
    </div>
  );
}

export function PromptDialog({
  title,
  label,
  placeholder,
  confirmLabel,
  onCancel,
  onConfirm,
  busy,
}: {
  title: string;
  label: string;
  placeholder?: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: (value: string) => void;
  busy?: boolean;
}) {
  const [value, setValue] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    onConfirm(trimmed);
  }

  return (
    <div className="dialog-backdrop" role="presentation" onClick={busy ? undefined : onCancel}>
      <form
        className="dialog-card"
        role="dialog"
        aria-labelledby="dialog-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h3 id="dialog-title">{title}</h3>
        <label htmlFor="dialog-input">{label}</label>
        <input
          id="dialog-input"
          autoFocus
          value={value}
          placeholder={placeholder}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!value.trim() || busy}
          >
            {busy ? 'Espere…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ActivityView({ events }: { events: ActivityItem[] }) {
  return (
    <section className="activity-view" aria-label="Actividad">
      <nav className="breadcrumb">
        <strong>Actividad</strong>
        <span className="sep">·</span>
        <span>Registro de auditoría de Respaldos</span>
      </nav>
      <div className="panel activity-list">
        {events.length === 0 ? (
          <div className="empty-state">
            Sin eventos de auditoría visibles para tu grupo.
          </div>
        ) : (
          events.map((a) => (
            <div className="activity-row" key={a.eventId}>
              <div className="act-avatar" style={{ background: a.color }}>
                {a.initials}
              </div>
              <div className="act-body">
                <div className="line">
                  <strong>{a.who}</strong> {a.action} <strong>{a.target}</strong>
                </div>
                <div className="when">{a.when}</div>
              </div>
              <span className={`act-badge ${a.kind}`}>
                {ACTIVITY_LABELS[a.kind] || a.kind}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/** Technical layout — admins only · no raw bucket URIs */
export function ArchitecturePanel() {
  return (
    <details className="s3-panel">
      <summary>
        Diseño del almacenamiento
        <span className="tag">solo admin</span>
      </summary>
      <pre className="s3-code">
        Marketing/{'\n'}
        {'  '}campañas/{'\n'}
        {'  '}video/{'\n'}
        {'  '}marca/{'\n'}
        {'  '}papelera/{'\n'}
        {'\n'}
        Respaldos · Bolivia / db/{'\n'}
        Respaldos · Perú / db/{'\n'}
        Respaldos · Paraguay / db/{'\n'}
        {'\n'}
        <span className="cmt">
          # Prefijos BO: KAPPA · MOTOR-SIAT · Novus · Varios · catercu ·
          detrocummins · komatcu
        </span>
      </pre>
    </details>
  );
}

export function StatusBar({
  text,
  isActivity,
  eventCount,
  showAccount,
}: {
  text: string;
  isActivity: boolean;
  eventCount: number;
  showAccount?: boolean;
}) {
  return (
    <footer className="status-bar">
      {isActivity ? (
        <span>
          <span className="accent">Auditoría</span> · últimos 7 días ·{' '}
          {eventCount} eventos mostrados
        </span>
      ) : (
        <span>
          <span className="accent">{text.split(' ')[0]}</span>
          {text.slice(text.indexOf(' '))}
        </span>
      )}
      {showAccount ? (
        <span className="footer-hint">Cuenta 656567185219 · región us-east-1</span>
      ) : (
        <span className="footer-hint">Grupo Galero · Respaldos</span>
      )}
    </footer>
  );
}
